import cx from 'classnames';
import flatten from 'lodash/flatten';
import get from 'lodash/get';
import partition from 'lodash/partition';
import uniq from 'lodash/uniq';
import PropTypes from 'prop-types';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { Card, Icon, Label } from 'semantic-ui-react';

import { userAgenda } from '../../agenda/store/agenda.selectors';
import Images from '../../utils/Images';
import { checkUserAvailability } from '../../utils/agendaUtils';
import { useItemValueColor } from '../../utils/designUtils';
import Tag from '../../workshops/components/Tag';
import { getClassName, getWorkshopString } from '../../workshops/utils';
import UserAvatars from '../Avatars/UserAvatars';
import EntityLink from '../EntityLink';
import { EntityTags } from '../EntityTags';
import QuotaField from './QuotaField';
import './WorkshopCard3.scss';
import WorkshopDate from './WorkshopDate';
import { WorkshopLiveBadge } from './WorkshopLiveBadge';

const translationPrefix = 'workshops.card';

const WorkshopCard3 = ({
  workshop,
  hasSessions,
  showSpeakers,
  showExhibitors,
  showDay,
  showDuration,
  showEndTime,
  showIcons,
  showLocation = false,
  showTimezone,
  speakersConfig,
  exhibitorsConfig,
  tagFields,
  disableRegisterTag,
  ...rest
}) => {
  const {
    _id,
    mandatory,
    endDate,
    exhibitors,
    image,
    productCategories,
    quota,
    shortDescription,
    speakers = [],
    startDate,
    technicityLevel,
    title,
    location,
    sessionLocation,
    usersCount,
    workshopStatus,
    isLive,
  } = workshop;
  const { dateConfig } = rest;
  const { color: categoryColor, style: categoryStyle } = useItemValueColor(
    'workshops',
    workshop,
    'category',
  );
  const { t } = useTranslation();
  const hasImage = image && image.uri;
  const agenda = useSelector(userAgenda);
  const { hasOverlappingEvent, isRegistered } = useMemo(() => {
    const [registeredEvent, otherEvents] = partition(
      agenda,
      (evt) => evt._id === _id || evt.workshopId === _id,
    );

    return {
      hasOverlappingEvent: !checkUserAvailability(otherEvents, workshop),
      isRegistered: registeredEvent.length > 0,
    };
  }, [agenda, workshop, _id]);

  const { extraFields = [], fields = ['speakers'] } = speakersConfig;
  const allSpeakers = useMemo(
    () => flatten(uniq([...fields, ...extraFields]).map((field) => get(workshop, field, []))),
    [workshop, fields, extraFields],
  );

  return (
    <div
      className={cx(
        'workshop--card3',
        getClassName(workshop),
        `session--${workshopStatus}`,
        hasOverlappingEvent && `session--has-overlapping-event`,
        isRegistered && `session--is-registered`,
        { 'no-image': !hasImage },
      )}
      style={{ '--ac-color-category': categoryColor }}
    >
      <Card link as={EntityLink} entity={workshop}>
        {!hasImage && <WorkshopLiveBadge sessionStatus={isLive} onImage variant="card3" />}
        {hasImage && (
          <div className="card__image">
            <WorkshopLiveBadge sessionStatus={isLive} onImage variant="card3" />
            <div
              className="image-with-shadow"
              style={{
                backgroundImage: `url(${Images.maxWidth(image, 800)})`,
              }}
            />
          </div>
        )}
        {hasImage && <Card.Content style={{ marginTop: 'calc(56.25% - 70px)', borderTop: 0 }} />}
        <Card.Content
          className={cx('borderless', { 'has-live-badge-corner': isLive && !hasImage })}
        >
          <div className="workshop--header">
            <p
              className={cx('workshop--category', { hasColor: !!categoryColor })}
              style={categoryStyle}
            >
              {getWorkshopString(workshop, 'category')}
            </p>
            <div style={{ flex: 1 }} />
            {!disableRegisterTag &&
              (isRegistered ? (
                <div className={cx('workshop--is-registered', { mandatory })}>
                  {t(`workshops.card.${mandatory ? 'mandatory' : 'registered'}`)}
                </div>
              ) : (
                <div className={cx('workshop--is-not-registered')}>
                  {t(`workshops.workshop.register`)}
                </div>
              ))}
          </div>
          <Card.Description style={{ marginTop: 8, marginBottom: 8 }}>
            <WorkshopDate
              startDate={startDate}
              endDate={endDate}
              showDay={showDay}
              showDuration={showDuration}
              showEndTime={showEndTime}
              showIcons={showIcons}
              showTimezone={showTimezone}
              dateConfig={dateConfig}
            />
            {/* location here for retro compatibility, delete this code when all events will used workshopSessions */}
            {location && showLocation && !sessionLocation && (
              <div className="location">
                <Icon name="map marker alternate" />
                {location}
              </div>
            )}
            {!location && showLocation && sessionLocation && (
              <div className="location">
                <Icon name="map marker alternate" />
                {sessionLocation}
              </div>
            )}
          </Card.Description>
          <QuotaField quota={quota} usersCount={usersCount} />
          <Card.Header style={{ fontSize: '1.2em', fontWeight: 'normal' }}>{title}</Card.Header>
          {showSpeakers && (
            <UserAvatars collection="speakers" userIds={allSpeakers} {...speakersConfig} />
          )}
          {showExhibitors && (
            <UserAvatars
              collection="exhibitors"
              imageKey="logo"
              userIds={exhibitors}
              {...exhibitorsConfig}
            />
          )}
          {tagFields?.length > 0 && (
            <EntityTags tagFields={tagFields} entity={workshop} type="workshop" />
          )}
          {/* Rename Show Exhibitors */}
          <div className="tags">
            {technicityLevel && <Label size="small">{technicityLevel}</Label>}
            {productCategories &&
              productCategories.length > 0 &&
              productCategories.map((pc) => (
                <Tag
                  key={pc}
                  size="small"
                  value={pc}
                  translationPrefix="workshop.tag.productCategories"
                />
              ))}
          </div>
          {/* <p style={{ lineHeight: '1.25', color: 'rgba(0, 0, 0, 0.5)', marginTop: 8 }}>
              Technicity Level : {technicityLevel}
            </p> */}
          <Card.Description className="shortDescription">{shortDescription}</Card.Description>
          {!hasSessions && (
            <div className="card__read-more">
              {t(`${translationPrefix}.read-more`, {
                context: isRegistered ? 'registered' : undefined,
              })}
            </div>
          )}
        </Card.Content>
      </Card>
    </div>
  );
};

WorkshopCard3.defaultProps = {
  hasSessions: false,
  showDay: true,
  showSpeakers: false,
  showExhibitors: false,
  showDuration: false,
  showEndTime: false,
  showTimezone: true,
  speakersConfig: {},
  exhibitorsConfig: {},
  disableRegisterTag: false,
};

WorkshopCard3.propTypes = {
  hasSessions: PropTypes.bool,
  showDay: PropTypes.bool,
  showSpeakers: PropTypes.bool,
  showExhibitors: PropTypes.bool,
  showDuration: PropTypes.bool,
  showEndTime: PropTypes.bool,
  showTimezone: PropTypes.bool,
  speakersConfig: PropTypes.object,
  exhibitorsConfig: PropTypes.object,
  disableRegisterTag: PropTypes.bool,
  showLive: PropTypes.bool,
  workshop: PropTypes.shape({
    _id: PropTypes.string.isRequired,
    category: PropTypes.oneOfType([
      PropTypes.arrayOf(PropTypes.string),
      PropTypes.string.isRequired,
    ]),
    collection: PropTypes.string.isRequired,
    endDate: PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(Date)]),
    exhibitors: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.object])),
    image: PropTypes.object,
    productCategories: PropTypes.array,
    location: PropTypes.string,
    quota: PropTypes.number,
    technicityLevel: PropTypes.string,
    title: PropTypes.string.isRequired,
    startDate: PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(Date)]),
    shortDescription: PropTypes.string,
    slug: PropTypes.string,
    speakers: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.object])),
    usersCount: PropTypes.number,
    workshopStatus: PropTypes.string,
    isLive: PropTypes.bool,
  }).isRequired,
};

export default WorkshopCard3;
